import IndexedDBService from './indexedDBService';

/**
 * Customer Database Service
 * Manages customer data in IndexedDB (offline-first)
 */
class CustomerDB extends IndexedDBService {
  constructor() {
    super('customerDB', 1);
  }

  protected setupObjectStores(db: IDBDatabase): void {
    // Customers Store
    if (!db.objectStoreNames.contains('customers')) {
      const customersStore = db.createObjectStore('customers', {
        keyPath: 'id',
      });
      customersStore.createIndex('storeId', 'storeId', { unique: false });
      customersStore.createIndex('phone', 'phone', { unique: false });
      customersStore.createIndex('email', 'email', { unique: false });
      customersStore.createIndex('segment', 'segment', { unique: false });
      customersStore.createIndex('timestamp', 'timestamp', { unique: false });
    }
  }

  /**
   * Save customer to IndexedDB
   */
  async saveCustomer(storeId: string, customer: any): Promise<void> {
    const customerData = {
      ...customer,
      storeId,
      timestamp: Date.now(),
    };
    await this.put('customers', customerData);
  }

  /**
   * Get customer by ID
   */
  async getCustomer(customerId: string): Promise<any> {
    return await this.get('customers', customerId);
  }

  /**
   * Save a page of customers in one local transaction.
   */
  async saveCustomers(storeId: string, customers: any[]): Promise<void> {
    const records = customers.filter((customer) => customer && customer.id);
    if (records.length === 0) return;
    const db = await this.getDB();
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(['customers'], 'readwrite');
      const store = transaction.objectStore('customers');
      records.forEach((customer) => {
        store.put({
          ...customer,
          storeId: customer.storeId || storeId,
          timestamp: customer.timestamp || Date.now(),
        });
      });
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('Customer cache write aborted'));
    });
  }

  /**
   * Get all customers for a store
   */
  async getAllCustomers(storeId: string): Promise<any[]> {
    if (!storeId) return [];
    try {
      return await this.queryByIndex('customers', 'storeId', storeId);
    } catch (error) {
      const allCustomers = await this.getAll('customers');
      return allCustomers.filter((customer) => customer.storeId === storeId);
    }
  }

  /**
   * Get customers by segment
   */
  async getCustomersBySegment(storeId: string, segment: string): Promise<any[]> {
    const customers = await this.getAllCustomers(storeId);
    return customers.filter(c => c.segment === segment);
  }

  /**
   * Delete customer
   */
  async deleteCustomer(customerId: string): Promise<void> {
    await this.delete('customers', customerId);
  }

  /**
   * Search customers by phone, email, or name
   */
  async searchCustomers(storeId: string, searchTerm: string): Promise<any[]> {
    const customers = await this.getAllCustomers(storeId);
    const term = searchTerm.toLowerCase();
    return customers.filter(c => 
      c.phone?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      // Search by single 'name' field if it exists
      c.name?.toLowerCase().includes(term) ||
      // Search by firstName + lastName if they exist
      `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase().includes(term)
    );
  }
}

const customerDB = new CustomerDB();
export default customerDB;

